import { useState } from 'react'

import { useMutation } from 'hooks/useMutation'

import { formatError } from 'utils/formatError'
import { Log } from 'utils/logger'
import { createProgram as createProgramQuery, fetchProgramById, postProgram } from 'utils/queries'
import { Toast } from 'utils/toast'

import { Program } from 'types/program'

import { useQuery } from './useQuery'

export const useProgram = (programId?: string) => {
  const [newProgramId, setNewProgramId] = useState<string | undefined>()

  const programQuery = useQuery<Program | undefined>(
    ['program', programId, newProgramId],
    async () => {
      if (!programId && !newProgramId) return undefined
      const programResult = await fetchProgramById(programId ?? newProgramId)
      if (!programResult) throw new Error('No program found')
      return programResult
    },
    {
      enabled: !!programId || !!newProgramId,
    },
  )

  const postProgramId = programQuery?.data?.id

  const createProgram = useMutation(
    async (data: string | undefined) => {
      const id = await createProgramQuery(data)
      if (!id) throw new Error('Error creating program')
      setNewProgramId(id ?? undefined)
      return { id }
    },
    {
      onSuccess: () => {
        programQuery?.refetch()
      },
      onError: (err) => {
        const formattedError = formatError(err)
        Toast.error('We had trouble creating program', formattedError)
        Log.error('useMutation onError', err)
      },
    },
  )

  const updateProgram = useMutation(
    (data: Program) =>
      postProgram({
        programId: data.id ?? postProgramId,
        updatedOrCreatedData: data,
      }),
    {
      onSuccess: () => {
        programQuery?.refetch()
      },
      onError: (err) => {
        const formattedError = formatError(err)
        Toast.error('We had trouble updating the program', formattedError)
        Log.error('useMutation onError', err)
      },
    },
  )

  return {
    ...programQuery,
    createProgram,
    updateProgram,
  }
}
